import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CloseButton, CustomModalContent, ToastTitleWrapper, PromoModal } from '../Promos.style';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import VoteButtonDataContainer from '../../PlayerOfTheGame/containers/VoteButtonDataContainer';
import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';
import useTracking from '@gameday/utils/hooks/useTracking';
import { useDispatch } from 'react-redux';
import updateShouldShowMessageAction from '../store/actions/updateShouldShowMessageAction';

const EMPTY = Object.freeze({});

// this is a variable that will be used to determine if the message should be shown or not
// it is beyond the toast message component life cycle. once onClose is called, the component will not be shown again.

const KEYDOWN = 'keydown';
const baseProps = {
    hideCloseButton: true,
    ContentComponent: CustomModalContent,
    backdropProps: { invisible: true },
    hideBackdrop: true,
    dataAttr: {
        'aria-modal': undefined,
        role: 'status',
        'aria-live': 'polite'
    },
    contentProps: {
        styleName: 'full',
        variants: {
            hide: {
                y: '100%',
                transition: { stiffness: 100 }
            },
            show: {
                y: 0,
                transition: { delay: 0.5, stiffness: 100 }
            }
        }
    },
    disableBackdropClick: true,
    disableScrollLock: true,
    position: { bottom: '0' }
};

const trackingBaseConfig = {
    text: 'Close',
    activity: 'Toast Message Close Button Click'
};

const ToastMessage = ({
    buttonKey,
    descriptionKey,
    portalTarget,
    trackingProps = EMPTY,
    rules,
    styleName,
    isOpenByDefault = false,
    shouldShowMessage
}) => {
    const description = useTranslator(descriptionKey, '');
    const buttonText = useTranslator(buttonKey, '');
    const [isOpen, setOpen] = useState(isOpenByDefault);
    const Tracking = useTracking();
    const isPOTG = rules === 'potg';

    const { componentName = [] } = trackingProps;
    const dispatch = useDispatch();

    const onClose = useCallback((e) => {
        setOpen(false);

        const trackingCfg = {
            componentName: isPOTG ? componentName.concat(['Player of the Game']) : componentName,
            ...trackingBaseConfig
        };
        if (e?.currentTarget) {
            Tracking.track.track_click(trackingCfg);
        }
        dispatch(updateShouldShowMessageAction(false));
    }, [setOpen, componentName, Tracking, isPOTG]);

    useEffect(() => {
        return () => {
            dispatch(updateShouldShowMessageAction(false));
        };
    }, []);

    useEffect(() => {
        let firstElement;
        let lastElement;

        const handleTabKeyPress = (event) => {
            if (event.key === 'Tab') {
                if (event.shiftKey && document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                } else if (
                    !event.shiftKey &&
                    document.activeElement === lastElement
                ) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        const handleEscapeKeyPress = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        if (portalTarget) {
            setOpen(true);
            const focusableElements = portalTarget?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements && focusableElements.length > 0) {
                firstElement = focusableElements[0];
                lastElement = focusableElements[focusableElements.length - 1];
                portalTarget.addEventListener(KEYDOWN, handleTabKeyPress);
                portalTarget.addEventListener(KEYDOWN, handleEscapeKeyPress);
            }
        }

        return () => {
            if (portalTarget) {
                portalTarget.removeEventListener(KEYDOWN, handleTabKeyPress);
                portalTarget.removeEventListener(KEYDOWN, handleEscapeKeyPress);
            }
        };
    }, [portalTarget, isOpen, setOpen]);

    const handlers = useMemo(() => ({ onClick: onClose }), [onClose]);

    const modalProps = useMemo(() => {
        if (!portalTarget && !styleName) {
            return baseProps;
        }

        return {
            ...baseProps,
            portalProps: { container: portalTarget },
            contentProps: {
                styleName
            },
            position: { bottom: styleName !== 'full' ? '16px' : '0' }
        };
    }, [portalTarget, styleName]);

    if (!shouldShowMessage) {
        return null;
    }

    return (
        <PromoModal {...modalProps} isOpen={isOpen} onClose={onClose}>
            <CloseButton
                handlers={handlers}
                iconName='close-thin'
                iconSize='24'
                size='sm'
                hasHover={false}
                ariaLabel='close'
                iconColor={'var(--gameday-active-icon-button-color)'}
                shape={ButtonShapes.HOLLOW_BORDERLESS}
            />
            <ToastTitleWrapper>{description}</ToastTitleWrapper>
            { isPOTG && <VoteButtonDataContainer trackingProps={trackingProps} label={buttonText} handlers={handlers} /> }

        </PromoModal>
    );
};

ToastMessage.propTypes = {
    buttonKey: PropTypes.string,
    descriptionKey: PropTypes.string,
    portalTarget: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
    trackingProps: PropTypes.object,
    rules: PropTypes.string,
    styleName: PropTypes.string,
    isOpenByDefault: PropTypes.bool,
    shouldShowMessage: PropTypes.bool
};

export default ToastMessage;